import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { BRAND } from "~/brand";
import Logo from "~/components/Logo";
import { PageTransition } from "~/components/PageTransition";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { client } from "~/lib/api";
import { PARTNER_KEYS } from "~/lib/partners";

// Backwards-compat: sign-up entry moved into the onboarding flow.
// Preserve any query params (UTM, ref, ...) so attribution survives.
const SearchParamsSchema = z.object({
  referralId: z.string().optional(),
  // Every known partner, not a subset: an unlisted value fails validation
  // here and the customer never reaches the onboarding flow below.
  partner: z.enum(PARTNER_KEYS).optional(),
  orderId: z.string().optional(),
  externalUserId: z.string().optional(),
  utm_source: z.string().optional(),
  utm_medium: z.string().optional(),
  utm_campaign: z.string().optional(),
});

export const Route = createFileRoute("/(auth-pages)/aanmelden-partner")({
  validateSearch: SearchParamsSchema,
  component: AuthForm,
  beforeLoad: async ({ search }) => {
    if (search.orderId) {
      throw redirect({
        to: "/aanmelden-partner",
        search: {
          ...search,
          referralId: search.orderId,
          orderId: undefined,
        },
        replace: true,
      });
    }

    if (search.externalUserId && search.partner) {
      const response = await client.api.partners["referral-by-external-id"].$get({
        query: {
          externalId: search.externalUserId,
          partner: search.partner,
        },
      });

      if (response.ok) {
        const { referralId } = await response.json();
        throw redirect({
          to: "/aanmelden-partner",
          search: {
            ...search,
            referralId,
            externalUserId: undefined,
          },
          replace: true,
        });
      }
    }

    if (search.referralId && search.partner) {
      throw redirect({
        to: "/onboarding/$step",
        params: { step: "provider-selection" },
        search,
      });
    }
  },
});

export function AuthForm() {
  const { referralId, partner } = Route.useSearch();

  if (!partner || !referralId) {
    return (
      <div className="bg-background relative flex w-full items-center justify-center px-4 pt-8 lg:min-h-screen lg:px-0 lg:pt-0">
        <div className="flex w-full max-w-md flex-col gap-6">
          <PageTransition className="w-full" delay={0}>
            <Logo className="-ml-3 lg:size-14" />
          </PageTransition>
          <PageTransition className="w-full" delay={100}>
            <Alert variant="destructive">
              <AlertDescription>
                Kan de partner of bijbehorende verwijzing niet vinden.
              </AlertDescription>
            </Alert>
          </PageTransition>
        </div>
        {BRAND.cornerPattern && (
          <img
            src="/corner-pattern.svg"
            className="invisible absolute bottom-0 left-0 opacity-50 lg:visible"
            alt=""
          />
        )}
      </div>
    );
  }
}
